import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { BUSINESS_TYPES, REVENUE, AD_SPEND } from "@/data";
import { trackLeadCreated } from "@/lib/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "", email: "", phone: "", company: "", website: "",
  business_type: "", monthly_revenue: "", monthly_ad_spend: "", message: "", nickname: "",
  utm_source: "", utm_medium: "", utm_campaign: "", utm_content: "", utm_term: "",
};

export default function LeadFormCard({ compact = false }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const utm = {
      utm_source: p.get("utm_source") || "",
      utm_medium: p.get("utm_medium") || "",
      utm_campaign: p.get("utm_campaign") || "",
      utm_content: p.get("utm_content") || "",
      utm_term: p.get("utm_term") || "",
    };
    if (Object.values(utm).some(Boolean)) setForm((f) => ({ ...f, ...utm }));
  }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const required = ["name", "email", "phone", "company", "business_type", "monthly_revenue", "monthly_ad_spend"];
    for (const k of required) {
      if (!form[k]) { toast.error("Please fill in all required fields."); return; }
    }
    if (submittingRef.current) return; // guard against double-submit
    submittingRef.current = true;
    setLoading(true);
    try {
      const { data: lead } = await axios.post(`${API}/leads`, form);
      if (typeof window !== "undefined" && window.fbq) {
        window.fbq("track", "Lead", { content_name: "Growth Audit", content_category: form.business_type });
      }
      // OpenAI Ads lead_created (post-success only) with hashed user matching data
      trackLeadCreated({ email: form.email, phone: form.phone, externalId: lead?.id });
      setDone(true);
      toast.success("Audit request received. We'll be in touch shortly.");
      setForm(EMPTY);
    } catch (err) {
      if (err.response?.status === 429) {
        toast.error("Too many submissions. Please try again in a little while.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  return (
    <div
      id="audit"
      data-testid="lead-form-card"
      className="scroll-mt-28 rounded-3xl border border-black/10 bg-white p-6 md:p-8 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.4)]"
    >
      {done ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex flex-col items-center text-center py-12"
          data-testid="lead-success"
        >
          <CheckCircle2 className="h-14 w-14 text-[#F48220]" />
          <h3 className="mt-6 font-display text-2xl font-bold">You're in.</h3>
          <p className="mt-2 font-body text-black/60 max-w-[36ch]">
            Our team will review your brand and reach out to schedule your audit readout.
          </p>
          <button
            onClick={() => setDone(false)}
            data-testid="submit-another-button"
            className="mt-8 rounded-full border border-black/15 px-6 py-3 font-body font-semibold hover:bg-black hover:text-white transition-colors duration-300"
          >
            Submit another
          </button>
        </motion.div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="lead-form">
          <div className="mb-1">
            <div className="font-body text-xs font-bold uppercase tracking-[0.2em] text-[#F48220]">Free Growth Audit</div>
            <h3 className="mt-2 font-display text-2xl font-extrabold tracking-tight">Find your revenue leaks</h3>
          </div>
          <input
            type="text"
            name="nickname"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={form.nickname}
            onChange={(e) => set("nickname")(e.target.value)}
            style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
          />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Your Name *">
              <Input data-testid="input-name" value={form.name} onChange={(e) => set("name")(e.target.value)} placeholder="Jane Founder" />
            </Field>
            <Field label="Business Email *">
              <Input data-testid="input-email" type="email" value={form.email} onChange={(e) => set("email")(e.target.value)} placeholder="jane@brand.com" />
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="WhatsApp Number *">
              <Input data-testid="input-phone" value={form.phone} onChange={(e) => set("phone")(e.target.value)} placeholder="+91 98765 43210" />
            </Field>
            <Field label="Brand / Company *">
              <Input data-testid="input-company" value={form.company} onChange={(e) => set("company")(e.target.value)} placeholder="Your brand" />
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Business Type *">
              <Picker testid="select-business-type" value={form.business_type} onChange={set("business_type")} options={BUSINESS_TYPES} placeholder="Select type" />
            </Field>
            <Field label="Monthly Ad Spend *">
              <Picker testid="select-ad-spend" value={form.monthly_ad_spend} onChange={set("monthly_ad_spend")} options={AD_SPEND} placeholder="Select range" />
            </Field>
          </div>
          <Field label="Monthly Revenue *">
            <Picker testid="select-revenue" value={form.monthly_revenue} onChange={set("monthly_revenue")} options={REVENUE} placeholder="Select range" />
          </Field>
          {!compact && (
            <>
              <Field label="Website URL">
                <Input data-testid="input-website" value={form.website} onChange={(e) => set("website")(e.target.value)} placeholder="https://yourbrand.com" />
              </Field>
              <Field label="Your biggest problem right now">
                <Textarea data-testid="input-message" value={form.message} onChange={(e) => set("message")(e.target.value)} placeholder="Tell us where it hurts..." rows={2} />
              </Field>
            </>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="submit-lead-button"
            className="group w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#0A0A0A] px-7 py-4 text-white font-body font-semibold disabled:opacity-60 hover-lift"
          >
            {loading ? "Sending..." : "Find My Revenue Leaks"}
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F48220] transition-transform duration-500 group-hover:rotate-45">
              <ArrowUpRight className="h-4 w-4 text-black" />
            </span>
          </button>
          <p className="text-center font-body text-xs text-black/40">No spam. No obligation. Just clarity.</p>
        </form>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="font-body text-sm font-semibold text-black/70">{label}</Label>
      {children}
    </div>
  );
}

function Picker({ value, onChange, options, placeholder, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o} value={o}>{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
